import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dailyPlansAPI } from '../services/api';

const LogPage = () => {
  const navigate = useNavigate();
  const [dailyPlan, setDailyPlan] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [totalActual, setTotalActual] = useState(0);
  const [adhocTitle, setAdhocTitle] = useState('');
  const [adhocDescription, setAdhocDescription] = useState('');
  const [adhocHours, setAdhocHours] = useState('');
  const [showAdhocForm, setShowAdhocForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [selectedProject, setSelectedProject] = useState('all');
  const [timeInputs, setTimeInputs] = useState({});

  useEffect(() => {
    loadTodayPlan();
  }, []);

  const convertToDecimal = (hours, minutes) => {
    const h = parseInt(hours) || 0;
    const m = parseInt(minutes) || 0;
    return h + (m / 60);
  };

  // const loadTodayPlan = async () => {
  //   try {
  //     const response = await dailyPlansAPI.getToday();
  //     setDailyPlan(response.data);
  //     setTasks(response.data.tasks || []);
  //     calculateTotalActual(response.data.tasks || []);
  //   } catch (error) {
  //     console.error('Failed to load daily plan:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const loadTodayPlan = async () => {
    try {
      const response = await dailyPlansAPI.getToday();

      setDailyPlan(response.data);
      setTasks(response.data.tasks || []);

      const initialInputs = {};

      (response.data.tasks || []).forEach(task => {
        const totalMinutes = Math.round((task.actualHours || 0) * 60);

        initialInputs[task.id] = {
          hours: Math.floor(totalMinutes / 60),
          minutes: totalMinutes % 60
        };
      });

      setTimeInputs(initialInputs);

      // Existing function
      calculateTotalActual(response.data.tasks || []);

    } catch (error) {
      console.error('Failed to load daily plan:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalActual = (taskList) => {
    const total = taskList.reduce((sum, task) => sum + (task.actualHours || 0), 0);
    setTotalActual(total);
  };

  // Auto-saves each field change immediately to backend
  const handleUpdateTask = async (taskId, field, value) => {
    try {
      await dailyPlansAPI.updateTaskLog(taskId, { [field]: value });
      const updatedTasks = tasks.map(task =>
        task.id === taskId ? { ...task, [field]: value } : task
      );
      setTasks(updatedTasks);
      calculateTotalActual(updatedTasks);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update task');
    }
  };

  // Finalizes log submission — called once when user is done
  const handleSubmitLogs = async () => {
    const hasAnyLog = tasks.some(task => (task.actualHours || 0) > 0);
    if (!hasAnyLog) {
      alert('Please enter hours for at least one task before submitting.');
      return;
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      const res = await dailyPlansAPI.submitLogs();
      setDailyPlan(res.data);
      setTasks(res.data.tasks || []);
      // alert(`Logs submitted! Total: ${totalActual.toFixed(1)}h`);
      alert(`Logs submitted! Total: ${formatToCustomTime(totalActual)}h`);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to submit logs');
    } finally {
      setSubmitting(false);
    }
  };

  // const handleCheckOut = async () => {

  //   if (checkingOut) return;
  //   setCheckingOut(true);
  //   try {
  //     await dailyPlansAPI.checkOut();
  //     alert('Checked out successfully! Have a great evening 👋');
  //     navigate('/');
  //   } catch (error) {
  //     console.log(error)
  //     console.log(error.response?.data);
  //     alert(error.response?.data?.message || 'Checkout failed');
  //   } finally {
  //     setCheckingOut(false);
  //   }
  // };


  const handleCheckOut = async () => {

    if (!isLogsSubmitted) {
      alert('Please submit logs first!');
      return;
    }

    const hasAtLeastOneLog = tasks.some(
      task => task.notes && task.notes.trim() !== ''
    );

    if (!hasAtLeastOneLog) {
      alert('Please write at least one task log before checkout!');
      return;
    }

    if (checkingOut) return;
    setCheckingOut(true);
    try {
      await dailyPlansAPI.checkOut();
      alert('Checked out successfully! Have a great evening 👋');
      navigate('/');
    } catch (error) {
      console.log(error)
      console.log(error.response?.data);
      alert(error.response?.data?.message || 'Checkout failed');
    } finally {
      setCheckingOut(false);
    }
  };

  const handleAddAdhocTask = async () => {
    if (!adhocTitle.trim()) {
      alert('Please enter a task title');
      return;
    }
    const hoursFloat = parseFloat(adhocHours);
    if (!hoursFloat || hoursFloat <= 0) {
      alert('Please enter valid hours');
      return;
    }
    try {
      await dailyPlansAPI.addTask({
        title: adhocTitle,
        description: adhocDescription || 'Ad-hoc task (not in original plan)',
        priority: 'MEDIUM',
        type: 'FEATURE',
        estimate: hoursFloat,
        isAdhoc: true,
        actualHours: hoursFloat,
        completed: true
      });
      setAdhocTitle('');
      setAdhocDescription('');
      setAdhocHours('');
      setShowAdhocForm(false);
      await loadTodayPlan();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add ad-hoc task');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  //  Derived from actual plan state (not hardcoded)
  const isLogsSubmitted = dailyPlan?.logsSubmitted;
  const isCheckedOut = dailyPlan?.checkedOut;

  const variance = (totalActual || 0) - (dailyPlan?.totalPlannedHours || 0);

  // All non-adhoc tasks must have actualHours > 0
  const allTasksLogged =
    tasks.length > 0 &&
    tasks.filter(task => !task.isAdhoc).every(task =>
      task.actualHours !== null &&
      task.actualHours !== undefined &&
      task.actualHours > 0
    );

  // const allTasksHaveLogs =
  //   tasks.length > 0 &&
  //   tasks.filter(task => !task.isAdhoc).every(task =>
  //     task.notes && task.notes.trim().length > 0
  //   );

  const projectList = [
    'all',
    ...new Set(tasks.map(task => task.backlogTask?.project?.name).filter(Boolean))
  ];

  const formatToCustomTime = (hours) => {
    if (isNaN(hours) || hours === null) return '0.00';

    const sign = hours < 0 ? '-' : '';
    const absHours = Math.abs(hours);

    const totalMinutes = Math.round(absHours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;

    return `${sign}${h}.${m.toString().padStart(2, '0')}`;
  };
  const parseCustomTime = (value) => {
    if (!value) return 0;

    const [h, m] = value.split('.').map(Number);
    return (h || 0) + ((m || 0) / 60);
  };

  return (
    <div className="app-container">
      <div className="container">
        <div className="header">
          <div className="header-top">
            <button className="btn-logout" onClick={() => navigate(-1)}>&larr; Back</button>
          </div>
          <div className="header-content">
            <h1>📝 Enter Daily Logs</h1>
            <p style={{ color: 'white', opacity: 0.9 }}>Log your work for today</p>
          </div>
        </div>

        <div style={{ padding: 24 }}>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>

            {/* ✅ Submitted banner */}
            {/* {isLogsSubmitted && !isCheckedOut && (
              <div style={{
                background: '#d1fae5', border: '2px solid #10b981',
                borderRadius: '10px', padding: '14px 20px', marginBottom: '20px',
                color: '#065f46', fontWeight: 600, display: 'flex',
                alignItems: 'center', gap: '10px'
              }}>
                ✅ Logs submitted. You can now Check-Out below.
              </div>
            )} */}

            {/* ✅ Checked out banner */}
            {isCheckedOut && (
              <div style={{
                background: '#dbeafe', border: '2px solid #3b82f6',
                borderRadius: '10px', padding: '14px 20px', marginBottom: '20px',
                color: '#1e40af', fontWeight: 600, display: 'flex',
                alignItems: 'center', gap: '10px'
              }}>
                🏁 You have already checked out for today.
              </div>
            )}

            {/* Summary card */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px'
            }}>
              <h3 style={{ fontSize: '18px', marginBottom: '12px' }}>Today's Summary</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Planned</div>
                  {/* <div style={{ fontSize: '24px', fontWeight: 700 }}>{(dailyPlan?.totalPlannedHours || 0).toFixed(1)}h</div> */}
                  <div style={{ fontSize: '24px', fontWeight: 700 }}> {formatToCustomTime(dailyPlan?.totalPlannedHours || 0)}h</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Logged</div>
                  {/* <div style={{ fontSize: '24px', fontWeight: 700 }}>{(totalActual || 0).toFixed(1)}h</div> */}
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>{formatToCustomTime(totalActual || 0)}h</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '4px' }}>Balance</div>
                  <div style={{ fontSize: '24px', fontWeight: 700 }}>
                    {/* {variance > 0 ? '+' : ''}{variance.toFixed(1)}h */}
                    {variance > 0 ? '+' : ''}{formatToCustomTime(variance)}h
                  </div>
                </div>
              </div>
            </div>

            {/* Project filter */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontWeight: 600, marginRight: '10px' }}>Filter by Project:</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '2px solid #e5e7eb' }}
              >
                {projectList.map((project, index) => (
                  <option key={index} value={project}>
                    {project === 'all' ? 'All Projects' : project}
                  </option>
                ))}
              </select>
            </div>

            {/* Task list */}
            <div>
              {tasks
                .filter(task => selectedProject === 'all' || task.backlogTask?.project?.name === selectedProject)
                .map(task => (
                  <div key={task.id} style={{
                    background: 'white', border: '2px solid #e5e7eb',
                    borderRadius: '12px', padding: '16px', marginBottom: '12px',
                    opacity: isCheckedOut ? 0.7 : 1
                  }}>
                    <div style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <div>
                          <strong>{task.title}</strong>
                          {task.backlogTask?.project?.name && (
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              📁 {task.backlogTask.project.name}
                            </div>
                          )}
                        </div>
                        <span className={`task-priority priority-${task.priority}`}>
                          {task.isFreeTime ? 'FREE TIME' : task.priority?.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '14px' }}>{task.description}</div>
                    </div>

                    {/* Estimate / Actual / Balance row */}
                    <div style={{
                      display: 'flex', gap: '20px', padding: '12px',
                      background: '#f9fafb', borderRadius: '8px', marginBottom: '12px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, marginBottom: '4px' }}>ESTIMATED</div>
                        {/* <div style={{ fontSize: '20px', fontWeight: 700 }}>{task.estimate}h</div> */}
                        <div style={{ fontSize: '20px', fontWeight: 700 }}>{formatToCustomTime(task.estimate)}h</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, marginBottom: '4px' }}>ACTUAL</div>
                        {/* <div style={{ fontSize: '20px', fontWeight: 700 }}>{task.actualHours || 0}h</div> */}
                        <div style={{ fontSize: '20px', fontWeight: 700 }}>{formatToCustomTime(task.actualHours || 0)}h</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 600, marginBottom: '4px' }}>BALANCE</div>
                        <div style={{
                          fontSize: '20px', fontWeight: 700,
                          color: (task.actualHours || 0) - task.estimate > 0 ? '#ef4444' : '#10b981'
                        }}>
                          {(task.actualHours || 0) - task.estimate > 0 ? '+' : ''}
                          {/* {(((task.actualHours || 0) - task.estimate) || 0).toFixed(1)}h */}
                          {formatToCustomTime((task.actualHours || 0) - task.estimate)}h
                        </div>
                      </div>
                    </div>

                    {/* Input controls — disabled after checkout */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                      <label style={{ fontWeight: 600, color: '#374151', fontSize: '14px', minWidth: '100px' }}>
                        Hours Spent:
                      </label>
                      {/* <input
                        type="number" min="0" max="24" step="0.5"
                        // value={task.actualHours || 0}
                        value={formatToCustomTime(task.actualHours || 0)}
                        disabled={isCheckedOut}
                        // onChange={(e) => handleUpdateTask(task.id, 'actualHours', parseFloat(e.target.value) || 0)}

                        onChange={(e) =>
                          handleUpdateTask(
                            task.id,
                            'actualHours',
                            parseCustomTime(e.target.value)
                          )
                        }
                        style={{
                          width: '80px', padding: '8px 12px',
                          border: '2px solid #e5e7eb', borderRadius: '6px',
                          fontSize: '14px', textAlign: 'center'
                        }}
                      /> */}

                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

                        {/* Hours Dropdown */}
                        <select
                          value={timeInputs[task.id]?.hours ?? 0}
                          onChange={(e) => {
                            const hours = parseInt(e.target.value);
                            const minutes = timeInputs[task.id]?.minutes || 0;

                            setTimeInputs(prev => ({
                              ...prev,
                              [task.id]: { hours, minutes }
                            }));

                            handleUpdateTask(
                              task.id,
                              'actualHours',
                              convertToDecimal(hours, minutes)
                            );
                          }}
                          style={{ padding: '6px', borderRadius: '6px' }}
                        >
                          {[0, 1, 2, 3, 4].map(h => (
                            <option key={h} value={h}>
                              {h.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </select>

                        :

                        {/* Minutes Dropdown */}
                        <select
                          value={timeInputs[task.id]?.minutes ?? 0}
                          onChange={(e) => {
                            const minutes = parseInt(e.target.value);
                            const hours = timeInputs[task.id]?.hours || 0;

                            setTimeInputs(prev => ({
                              ...prev,
                              [task.id]: { hours, minutes }
                            }));

                            handleUpdateTask(
                              task.id,
                              'actualHours',
                              convertToDecimal(hours, minutes)
                            );
                          }}
                          style={{ padding: '6px', borderRadius: '6px' }}
                        >
                          {[0, 15, 30, 45].map(m => (
                            <option key={m} value={m}>
                              {m.toString().padStart(2, '0')}
                            </option>
                          ))}
                        </select>

                      </div>

                      <label style={{ marginLeft: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="checkbox"
                          checked={task.completed || false}
                          disabled={isCheckedOut}
                          onChange={(e) => handleUpdateTask(task.id, 'completed', e.target.checked)}
                          style={{ width: 'auto' }}
                        />
                        Mark as Completed
                      </label>
                    </div>

                    <textarea
                      placeholder="Add notes about this task..."
                      value={task.notes || ''}
                      disabled={isCheckedOut}
                      onChange={(e) => handleUpdateTask(task.id, 'notes', e.target.value)}
                      style={{
                        width: '100%', padding: '10px 12px',
                        border: '2px solid #e5e7eb', borderRadius: '6px',
                        fontSize: '14px', resize: 'vertical', minHeight: '60px'
                      }}
                    />
                  </div>
                ))}
            </div>

            {/* Ad-hoc task — hidden after checkout */}
            {!isCheckedOut && (
              !showAdhocForm ? (
                <div
                  onClick={() => setShowAdhocForm(true)}
                  style={{
                    background: '#f3f4f6', border: '2px dashed #cbd5e1',
                    borderRadius: '12px', padding: '20px', textAlign: 'center',
                    cursor: 'pointer', marginTop: '12px'
                  }}
                >
                  <div style={{ color: '#6b7280', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span>➕</span> Add Ad-hoc Task (not in plan)
                  </div>
                </div>
              ) : (
                <div style={{
                  background: '#f9fafb', border: '2px solid #e5e7eb',
                  borderRadius: '12px', padding: '16px', marginTop: '12px'
                }}>
                  <h4 style={{ marginBottom: '12px' }}>Add Ad-hoc Task</h4>
                  <div className="form-group">
                    <label>Task Title *</label>
                    <input type="text" value={adhocTitle} onChange={(e) => setAdhocTitle(e.target.value)} placeholder="Enter task title" />
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea value={adhocDescription} onChange={(e) => setAdhocDescription(e.target.value)} placeholder="What did you work on?" style={{ minHeight: '60px' }} />
                  </div>
                  <div className="form-group">
                    <label>Actual Time Spent (hours) *</label>
                    <input type="number" min="0.5" max="8" step="0.05" value={adhocHours} onChange={(e) => setAdhocHours(e.target.value)} placeholder="e.g., 2" />
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-cancel" onClick={() => setShowAdhocForm(false)}>Cancel</button>
                    <button className="btn btn-submit" onClick={handleAddAdhocTask}>Add to Logs</button>
                  </div>
                </div>
              )
            )}

            {/* ✅ Action buttons — single clean row */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn btn-cancel" onClick={() => navigate(-1)}>
                {isCheckedOut ? 'Back' : 'Cancel'}
              </button>

              {/* Submit Logs: visible until logs are submitted */}
              {/* {!isLogsSubmitted && !isCheckedOut && (
                <button
                  className="btn btn-submit"
                  onClick={handleSubmitLogs}
                  disabled={submitting}
                  style={{ opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Logs'}
                </button>
              )} */}

              {!isCheckedOut && (
                <button
                  className="btn btn-submit"
                  onClick={handleSubmitLogs}
                  disabled={submitting}
                  style={{ opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Logs'}
                </button>
              )}

              {/* Check-Out: visible only when logs submitted AND all tasks have hours logged */}

              {/* {isLogsSubmitted && allTasksLogged && !isCheckedOut && (

                <button
                  className="btn btn-checkout"
                  onClick={handleCheckOut}
                  disabled={checkingOut}
                  style={{ opacity: checkingOut ? 0.6 : 1 }}
                >
                  {checkingOut ? 'Checking Out...' : '🚪 Check-Out'}
                </button>
              )} */}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default LogPage;